<template>
  <div class="container page fund-record-page">
    <van-nav-bar :title="$t('moneylog.title')" class="nav-bar" :border="false">
      <template #left>
        <van-icon name="arrow-left" @click="back()" />
      </template>
    </van-nav-bar>

    <div class="main-content">
      <van-tabs
        v-model="activeTab"
        class="record-tabs"
        background="#fff"
        color="#d63f8c"
        title-active-color="#d63f8c"
        title-inactive-color="#999"
        line-width="1.6rem"
        @change="onTabChange"
      >
        <van-tab :title="$t('my.recharge_record')" name="recharge">
          <van-pull-refresh v-model="isLoadingRecharge" @refresh="onRefreshRecharge">
            <van-empty v-if="rechargeList.length === 0" :description="$t('withdraw.empty_data')" />
            <div v-else class="record-card" v-for="(item, index) in rechargeList" :key="'r' + index">
              <div class="card-top">
                <span class="status-text" :class="{ 'is-success': item.status === 2 }">{{ statusText(item) }}</span>
                <span class="amount-text positive">{{ $t('withdraw.money') }}: +{{ item.money }}</span>
              </div>
              <div class="card-line">{{ $t('withdraw.submit_time') }}: {{ item.create_time }}</div>
              <div class="card-line">{{ $t('withdraw.check_time') }}: {{ item.update_time }}</div>
            </div>
          </van-pull-refresh>
        </van-tab>

        <van-tab :title="$t('my.withdraw_record')" name="withdraw">
          <van-pull-refresh v-model="isLoadingWithdraw" @refresh="onRefreshWithdraw">
            <van-empty v-if="withdrawList.length === 0" :description="$t('withdraw.empty_data')" />
            <div v-else class="record-card" v-for="(item, index) in withdrawList" :key="'w' + index">
              <div class="card-top">
                <span class="status-text" :class="{ 'is-success': item.status === 2 }">{{ statusText(item) }}</span>
                <span class="amount-text">{{ $t('withdraw.money') }}: -{{ item.money }}</span>
              </div>
              <div class="card-line" v-if="item.desc">{{ $t('withdraw.desc') }}: {{ item.desc }}</div>
              <div class="card-line">{{ $t('withdraw.submit_time') }}: {{ item.create_time }}</div>
              <div class="card-line">{{ $t('withdraw.check_time') }}: {{ item.update_time }}</div>
            </div>
          </van-pull-refresh>
        </van-tab>

        <van-tab :title="$t('reservation.task_list')" name="task">
          <van-pull-refresh v-model="isLoadingTask" @refresh="onRefreshTask">
            <van-empty v-if="taskList.length === 0" :description="$t('withdraw.empty_data')" />
            <div v-else class="game-record-card" v-for="item in taskList" :key="item.id">
              <div class="gr-header">
                <span class="gr-id">{{ item.expect }}</span>
                <span class="gr-name">{{ item.name }}</span>
              </div>
              <div class="gr-result" v-if="item.opencode && item.opencode.length >= 3">
                <div class="gr-dice">
                  <img v-for="(n, di) in item.opencode" :key="di" :src="diceImg(n)" alt="" />
                </div>
                <div class="gr-result-text">
                  <span>{{ getSum(item.opencode) }}</span>
                  <span>{{ getSizeLabel(item.opencode) }}</span>
                  <span>{{ getDoubleLabel(item.opencode) }}</span>
                </div>
              </div>
              <div class="gr-status-row">
                <span class="gr-status" :class="{ pending: item.status !== 1 }">{{ getTaskStatus(item) }}</span>
                <span class="gr-amount">{{ $t('game_record.bet_amount') }}: {{ formatBetMoney(item.money) }}</span>
              </div>
              <div class="gr-bet-row" v-if="getBetContent(item)">
                <span class="gr-bet-label">{{ $t('game_record.bet_content') }}:</span>
                <span class="gr-bet-value">{{ getBetContent(item) }}</span>
              </div>
              <div class="gr-time">{{ $t('reservation.order_time') }}: {{ item.create_time }}</div>
              <div class="gr-time">{{ $t('reservation.settle_time') }}: {{ item.update_time }}</div>
            </div>
          </van-pull-refresh>
        </van-tab>
      </van-tabs>
    </div>
  </div>
</template>

<script>
import { getBetContent, formatBetMoney } from '@/utils/gameBet'

export default {
  name: 'FundRecord',
  data() {
    return {
      activeTab: 'recharge',
      isLoadingRecharge: false,
      isLoadingWithdraw: false,
      isLoadingTask: false,
      rechargeList: [],
      withdrawList: [],
      taskList: []
    }
  },
  methods: {
    back() {
      this.$router.go(-1)
    },
    statusText(item) {
      if (item.status === 1) return this.$t('my.audit_waiting')
      if (item.status === 2) return this.$t('my.audit_success')
      if (item.status === 3) return this.$t('my.audit_reject')
      return item.status_text || ''
    },
    onTabChange(name) {
      if (name === 'recharge' && !this.rechargeList.length) this.getRechargeList()
      if (name === 'withdraw' && !this.withdrawList.length) this.getWithdrawList()
      if (name === 'task' && !this.taskList.length) this.getTaskList()
    },
    onRefreshRecharge() {
      this.getRechargeList()
    },
    onRefreshWithdraw() {
      this.getWithdrawList()
    },
    onRefreshTask() {
      this.getTaskList()
    },
    getRechargeList() {
      this.isLoadingRecharge = true
      this.$http({ method: 'get', url: 'user_get_recharge_list' }).then(res => {
        this.isLoadingRecharge = false
        if (res.code === 200) this.rechargeList = res.data || []
        else if (res.code === 401) this.$toast(res.msg)
      }).catch(() => { this.isLoadingRecharge = false })
    },
    getWithdrawList() {
      this.isLoadingWithdraw = true
      this.$http({ method: 'get', url: 'user_get_withdraw_list' }).then(res => {
        this.isLoadingWithdraw = false
        if (res.code === 200) this.withdrawList = res.data || []
        else if (res.code === 401) this.$toast(res.msg)
      }).catch(() => { this.isLoadingWithdraw = false })
    },
    getTaskList() {
      this.isLoadingTask = true
      this.$http({ method: 'get', url: 'user_get_game_list' }).then(res => {
        this.isLoadingTask = false
        if (res.code === 200) {
          const raw = res.data || []
          this.taskList = Array.isArray(raw) ? raw : Object.values(raw)
        } else if (res.code === 401) {
          this.$toast(res.msg)
        }
      }).catch(() => { this.isLoadingTask = false })
    },
    diceImg(num) {
      return `/img/lottery/shaizi/${num}.png`
    },
    getSum(opencode) {
      if (!opencode || opencode.length < 3) return ''
      return opencode[0] + opencode[1] + opencode[2]
    },
    getSizeLabel(opencode) {
      const sum = this.getSum(opencode)
      return sum >= 11 && sum <= 18 ? '甲' : '乙'
    },
    getDoubleLabel(opencode) {
      const sum = this.getSum(opencode)
      return sum % 2 === 0 ? '丁' : '丙'
    },
    getBetContent,
    formatBetMoney,
    getTaskStatus(item) {
      if (item.status === 1) return this.$t('game_record.status_completed')
      return item.status_text || this.$t('my.audit_waiting')
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.push({ path: '/Login' })
    } else {
      this.getRechargeList()
    }
  }
}
</script>

<style lang="less" scoped>
@import '../../assets/css/base.css';

.fund-record-page {
  .main-content {
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  /deep/ .record-tabs {
    .van-tabs__nav--line {
      padding-bottom: 0;
    }

    .van-tab {
      flex: 1 1 33.33%;
      max-width: 33.33%;
    }

    .van-tab__text {
      transform: none !important;
    }
  }
}
</style>
